/**
 * A brand name offered from the style's vendor (2026-10-02).
 *
 * "When I go to add a brand on a style I only see 2 brands, but purchase
 * orders have a ton of brands." Those are vendors: the shop's export carried
 * no brand column, so 4,078 of 4,079 styles have none, while the 106 vendors
 * came in with the stock. Most of this shop's vendors *are* the maker —
 * ROSSIGNOL, BATALEON, GIRO SPORT DESIGN — so the vendor's name, tidied, is
 * the likeliest brand. Offered, never applied: a distributor such as EASTERN
 * SKATEBOARD SUPPLY carries a dozen brands, and only a person knows which.
 */

/** Company-form words a brand name never carries. */
const LEGAL = /^(inc|llc|ltd|limited|corp|corporation|co|company|gmbh|sa|ag)$/i;

/** Small words left lower-case inside a name, as a person writes them. */
const SMALL = new Set(['and', 'of', 'the', 'for', 'de']);

export function brandFromVendor(vendor: string | null | undefined): string | null {
  if (vendor == null) return null;
  const words = vendor
    .trim()
    .replace(/[.,]+$/, '')
    .split(/\s+/)
    .filter((word) => word !== '');
  while (words.length > 1 && LEGAL.test(words[words.length - 1]!.replace(/[.,]/g, ''))) {
    words.pop();
  }
  if (words.length === 0 || /^unassigned$/i.test(words.join(' '))) return null;

  // Only shouting is recased: "Pit Viper" and "Starling" were typed with care.
  const shouting = words.join(' ') === words.join(' ').toUpperCase();
  const name = shouting
    ? words
        .map((word, index) => {
          const lower = word.toLowerCase().replace(/[,]+$/, '');
          if (index > 0 && SMALL.has(lower)) return lower;
          // No vowel is an initialism — NHS, KRK — and stays as it was.
          if (/^[^aeiouy]+$/i.test(lower)) return word.replace(/[,]+$/, '');
          return lower.replace(
            /(^|[-/&])([a-z])/g,
            (_, edge: string, letter: string) => `${edge}${letter.toUpperCase()}`,
          );
        })
        .join(' ')
    : words.join(' ').replace(/,+$/, '');
  return name === '' ? null : name;
}
